// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState } from 'react';
import Modal from './Modal';
import './UpgradeShop.css';
import Button from './Button';
import { useUpgrades } from '../contexts/UpgradesContext';
import { useCredits } from '../contexts/CreditsContext';
import { useFuel } from '../contexts/FuelContext';
import { usePurchaseConfirm, PurchaseConfirmBanner } from '../hooks/ui/usePurchaseConfirm';

const UpgradeShop = ({ isOpen, onClose, location }) => {
  const { credits: currentCredits, removeCredits } = useCredits();
  const { getAvailableUpgrades, hasUpgrade, canPurchase, purchaseUpgrade, getPurchasedUpgrades } = useUpgrades();
  const { purchaseTankUpgrade, getTankUpgradeInfo } = useFuel();
  const { pending, requestPurchase, confirm, cancel } = usePurchaseConfirm();
  
  const [selectedCategory, setSelectedCategory] = useState('shields');
  const [purchaseMessage, setPurchaseMessage] = useState('');

  // Early return if modal not open or no location
  if (!isOpen || !location) return null;

  const categories = [
    { id: 'shields', name: 'Shields', icon: '🛡️', color: '#00ffff' },
    { id: 'hull', name: 'Hull', icon: '🔩', color: '#00ff88' },
    { id: 'engines', name: 'Engines', icon: '🚀', color: '#ffaa00' },
    { id: 'sensors', name: 'Sensors', icon: '📡', color: '#ff00ff' },
    { id: 'special', name: 'Special', icon: '⚡', color: '#ff4444' }
  ];

  // Determine location type for availability
  const locationType = location.type === 'station' ? 'station' : 'planet';

  // Get available upgrades for this location
  const availableUpgrades = getAvailableUpgrades(locationType);
  
  // Get purchased upgrades
  const purchasedUpgrades = getPurchasedUpgrades();
  
  // Filter available upgrades to only show unpurchased ones
  const unpurchasedUpgrades = availableUpgrades.filter(u => !hasUpgrade(u.id));
  
  // Filter by selected category for available upgrades
  const categoryUpgrades = unpurchasedUpgrades.filter(u => u.category === selectedCategory);
  
  // Get purchased upgrades for current category
  const purchasedCategoryUpgrades = purchasedUpgrades.filter(u => u.category === selectedCategory);

  const handlePurchase = (upgradeId) => {
    const upgrade = categoryUpgrades.find(u => u.id === upgradeId);
    if (!upgrade) return;

    // Check if can purchase
    if (!canPurchase(upgradeId)) {
      if (upgrade.requires) {
        const reqUpgrade = availableUpgrades.find(u => u.id === upgrade.requires);
        setPurchaseMessage(`Requires ${reqUpgrade?.name || 'previous upgrade'} first!`);
      } else {
        setPurchaseMessage('Already purchased!');
      }
      setTimeout(() => setPurchaseMessage(''), 3000);
      return;
    }

    // Check credits
    if (currentCredits < upgrade.cost) {
      setPurchaseMessage(`Insufficient credits! Need ${upgrade.cost} credits.`);
      setTimeout(() => setPurchaseMessage(''), 3000);
      return;
    }

    // Special handling for fuel tank upgrades
    if (upgradeId === 'fuel_tank_1' || upgradeId === 'fuel_tank_2') {
      const tankInfo = getTankUpgradeInfo();
      if (!tankInfo.canUpgrade) {
        setPurchaseMessage(tankInfo.reason || 'Cannot upgrade fuel tanks!');
        setTimeout(() => setPurchaseMessage(''), 3000);
        return;
      }
      
      // Deduct credits and purchase tank upgrade
      removeCredits(upgrade.cost, `upgrade: ${upgrade.name}`);
      const result = purchaseTankUpgrade();
      
      if (result.success) {
        // Also mark as purchased in upgrades context
        purchaseUpgrade(upgradeId);
        setPurchaseMessage(`✓ ${upgrade.name} installed! Fuel capacity increased to ${tankInfo.nextMax.toFixed(0)}%`);
        setTimeout(() => setPurchaseMessage(''), 4000);
      } else {
        setPurchaseMessage(result.error || 'Failed to upgrade fuel tanks!');
        setTimeout(() => setPurchaseMessage(''), 3000);
      }
      return;
    }

    // Deduct credits (with confirmation for expensive items)
    const executePurchase = () => {
      removeCredits(upgrade.cost, `upgrade: ${upgrade.name}`);
      const result = purchaseUpgrade(upgradeId);
      if (result.success) {
        setPurchaseMessage(`✓ ${upgrade.name} installed! Cost: ${upgrade.cost} credits`);
        setTimeout(() => setPurchaseMessage(''), 4000);
      } else {
        setPurchaseMessage(result.message);
        setTimeout(() => setPurchaseMessage(''), 3000);
      }
    };
    requestPurchase(upgrade.name, upgrade.cost, executePurchase);
  };

  const getUpgradeStatus = (upgrade) => {
    const purchased = hasUpgrade(upgrade.id);
    const canPurch = canPurchase(upgrade.id);
    const canAfford = currentCredits >= upgrade.cost;

    if (purchased) return 'purchased';
    if (upgrade.comingSoon) return 'coming-soon';
    if (!canPurch) return 'locked';
    if (!canAfford) return 'insufficient';
    return 'available';
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <>
            <span className="upgrade-shop-icon">🔧</span>
            SHIP UPGRADES
          </>
        }
        size="large"
        className="upgrade-shop-modal"
      >
        {/* Info Bar */}
        <div className="upgrade-shop-info">
          <div className="info-item">
            <span className="info-label">Location:</span>
            <span className="info-value">{location.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Credits:</span>
            <span className="info-value credits">{currentCredits.toLocaleString()} CR</span>
          </div>
          <div className="info-item">
            <span className="info-label">Installed:</span>
            <span className="info-value">{purchasedUpgrades.length} upgrades</span>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="upgrade-categories">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant="ghost"
              size="small"
              className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                borderColor: selectedCategory === cat.id ? cat.color : 'rgba(0, 255, 255, 0.3)',
                color: selectedCategory === cat.id ? cat.color : '#00ffff'
              }}
            >
              <span className="category-icon">{cat.icon}</span>
              <span className="category-name">{cat.name}</span>
            </Button>
          ))}
        </div>

        {/* Purchase Confirmation */}
        <PurchaseConfirmBanner pending={pending} onConfirm={confirm} onCancel={cancel} />

        {/* Purchase Feedback */}
        {purchaseMessage && (
          <div className={`upgrade-feedback ${purchaseMessage.includes('✓') ? 'success' : 'error'}`}>
            {purchaseMessage}
          </div>
        )}

        {/* Installed Upgrades Section */}
        {purchasedCategoryUpgrades.length > 0 && (
          <div className="installed-upgrades-section">
            <h3>INSTALLED UPGRADES</h3>
            <div className="installed-upgrades-grid">
              {purchasedCategoryUpgrades.map(upgrade => (
                <div key={upgrade.id} className="installed-upgrade-card">
                  <span className="upgrade-icon">{upgrade.icon}</span>
                  <span className="upgrade-name">{upgrade.name}</span>
                  <span className="upgrade-tier-badge">T{upgrade.tier}</span>
                  <span className="upgrade-status">✓ ACTIVE</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Available Upgrades */}
        <div className="upgrades-list-section">
          <h3>AVAILABLE UPGRADES</h3>
          <div className="upgrades-list">
          {categoryUpgrades.length === 0 ? (
            <div className="no-upgrades">
              <div className="no-upgrades-icon">
                {purchasedCategoryUpgrades.length > 0 ? '✓' : '🚫'}
              </div>
              <div className="no-upgrades-text">
                {purchasedCategoryUpgrades.length > 0 
                  ? `All ${selectedCategory} upgrades purchased! Check other categories or visit advanced stations.`
                  : `No ${selectedCategory} upgrades available at this location. Visit advanced stations for more options.`}
              </div>
            </div>
          ) : (
            categoryUpgrades.map(upgrade => {
              const status = getUpgradeStatus(upgrade);
              const canAfford = currentCredits >= upgrade.cost;

              return (
                <div key={upgrade.id} className={`upgrade-card ${status}`}>
                  <div className="upgrade-card-header">
                    <div className="upgrade-icon-container">
                      {upgrade.category && (
                        <img 
                          src={`/icons/modules/${upgrade.category === 'shields' ? 'shield-generator' : 
                                                upgrade.category === 'hull' ? 'life-support' :
                                                upgrade.category === 'engines' ? 'engine-upgrade' :
                                                upgrade.category === 'cargo' ? 'cargo-hold' :
                                                upgrade.category === 'fuelTank' ? 'fuel-tank' : 
                                                'navigation-computer'}.svg`}
                          alt={upgrade.name}
                          className="upgrade-icon-svg"
                        />
                      )}
                      <span className="upgrade-icon-emoji">{upgrade.icon}</span>
                    </div>
                    <div className="upgrade-info">
                      <div className="upgrade-name-row">
                        <span className="upgrade-name">{upgrade.name}</span>
                        <span className="upgrade-tier">TIER {upgrade.tier}</span>
                      </div>
                      <div className="upgrade-description">{upgrade.description}</div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="upgrade-stats">
                    {upgrade.shieldCapacity && (
                      <div className="stat">
                        <span className="stat-label">Shield Capacity:</span>
                        <span className="stat-value">+{upgrade.shieldCapacity}</span>
                      </div>
                    )}
                    {upgrade.rechargeRate && (
                      <div className="stat">
                        <span className="stat-label">Recharge Rate:</span>
                        <span className="stat-value">{upgrade.rechargeRate}/s</span>
                      </div>
                    )}
                    {upgrade.hullBonus && (
                      <div className="stat">
                        <span className="stat-label">Hull Bonus:</span>
                        <span className="stat-value">+{upgrade.hullBonus}</span>
                      </div>
                    )}
                    {upgrade.damageReduction && (
                      <div className="stat">
                        <span className="stat-label">Damage Reduction:</span>
                        <span className="stat-value">{(upgrade.damageReduction * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {upgrade.warpSpeedBonus && (
                      <div className="stat">
                        <span className="stat-label">Warp Speed:</span>
                        <span className="stat-value">+{(upgrade.warpSpeedBonus * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {upgrade.fuelEfficiency && (
                      <div className="stat">
                        <span className="stat-label">Fuel Efficiency:</span>
                        <span className="stat-value">-{(upgrade.fuelEfficiency * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {upgrade.scanBonus && (
                      <div className="stat">
                        <span className="stat-label">Scan Bonus:</span>
                        <span className="stat-value">+{(upgrade.scanBonus * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {upgrade.threatDetection && (
                      <div className="stat">
                        <span className="stat-label">Special:</span>
                        <span className="stat-value">Threat Detection</span>
                      </div>
                    )}
                    {upgrade.repairRate && (
                      <div className="stat">
                        <span className="stat-label">Auto-Repair:</span>
                        <span className="stat-value">{upgrade.repairRate} HP/10s</span>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="upgrade-card-footer">
                    <div className="upgrade-cost">
                      <span className="cost-icon">💰</span>
                      <span className="cost-amount">{upgrade.cost}</span>
                      <span className="cost-label">credits</span>
                    </div>

                    {status === 'purchased' && (
                      <div className="upgrade-status purchased">
                        <span className="status-icon">✓</span>
                        INSTALLED
                      </div>
                    )}

                    {status === 'coming-soon' && (
                      <div className="upgrade-status coming-soon">
                        COMING SOON
                      </div>
                    )}

                    {status === 'locked' && (
                      <div className="upgrade-status locked">
                        <span className="status-icon">🔒</span>
                        LOCKED
                      </div>
                    )}

                    {(status === 'available' || status === 'insufficient') && (
                      <Button
                        variant={canAfford ? 'success' : 'secondary'}
                        size="small"
                        className="upgrade-button"
                        onClick={() => handlePurchase(upgrade.id)}
                        disabled={!canAfford}
                      >
                        {canAfford ? 'PURCHASE' : 'INSUFFICIENT CREDITS'}
                      </Button>
                    )}
                  </div>

                  {/* Requirement indicator */}
                  {upgrade.requires && status === 'locked' && (
                    <div className="upgrade-requirement">
                      Requires previous tier upgrade
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        </div>

        {/* Footer */}
        <div className="upgrade-shop-footer">
          <Button 
            variant="secondary" 
            icon="←"
            onClick={onClose}
            className="done-button"
          >
            BACK TO STATION
          </Button>
        </div>
      </Modal>
  
      {/* Purchase Message Modal */}
      {purchaseMessage && (
        <Modal
          isOpen={!!purchaseMessage}
          onClose={() => setPurchaseMessage('')}
          size="small"
          showCloseButton={false}
          className={`upgrade-message-modal ${purchaseMessage.includes('Insufficient') || purchaseMessage.includes('Requires') || purchaseMessage.includes('Already') ? 'error' : 'success'}`}
        >
          {purchaseMessage}
        </Modal>
      )}
    </>
  );
};

export default UpgradeShop;
